import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  SelectChangeEvent,
  CircularProgress
} from '@mui/material';
import { ACLAuthorization, AccessMode } from '@/api/bol';

interface ACLAuthorizationEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (memberIdentifier: string, mode: AccessMode, authorizedBy: string) => Promise<void>;
  authorization: ACLAuthorization;
}

const ACCESS_MODES: AccessMode[] = ['READ', 'WRITE', 'CONTROL'];

const ACLAuthorizationEditDialog: React.FC<ACLAuthorizationEditDialogProps> = ({
  open,
  onClose,
  onSubmit,
  authorization
}) => {
  const [mode, setMode] = useState<AccessMode>(authorization.mode);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMode(authorization.mode);
      setError(null);
    }
  }, [open, authorization]);

  const handleSubmit = async () => {
    setError(null);

    if (!mode) {
      setError('Access mode is required');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(authorization.memberIdentifier, mode, authorization.authorizedBy);
      handleClose();
    } catch (err) {
      console.error('Failed to update ACL authorization:', err);
      setError(err instanceof Error ? err.message : 'Failed to update authorization');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={submitting}
    >
      <DialogTitle>Edit ACL Authorization</DialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            label="Member Identifier"
            value={authorization.memberIdentifier}
            disabled
            fullWidth
            helperText="Member identifier cannot be changed"
          />

          <TextField
            label="Authorized By"
            value={authorization.authorizedBy}
            disabled
            fullWidth
            helperText="Authorization creator cannot be changed"
          />

          <FormControl fullWidth required>
            <InputLabel id="access-mode-label">Access Mode</InputLabel>
            <Select
              labelId="access-mode-label"
              value={mode}
              onChange={(e: SelectChangeEvent<AccessMode>) => setMode(e.target.value as AccessMode)}
              label="Access Mode"
              disabled={submitting}
            >
              {ACCESS_MODES.map((accessMode) => (
                <MenuItem key={accessMode} value={accessMode}>
                  {accessMode}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Alert severity="info">
            <strong>READ:</strong> Can read the logistics object<br />
            <strong>WRITE:</strong> Can read and propose modifications<br />
            <strong>CONTROL:</strong> Can read, write, and manage ACL
          </Alert>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? <CircularProgress size={24} /> : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ACLAuthorizationEditDialog;
